import { ArrayUtility } from '@reapptor/ts-linq';
import { PagedListUtility } from '@reapptor/ts-paged-list';
import { Dictionary } from 'typescript-collections';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class AthenaeumConstants {}
AthenaeumConstants.badRequestStatusCode = 400;
AthenaeumConstants.unauthorizedStatusCode = 401;
AthenaeumConstants.forbiddenStatusCode = 403;
AthenaeumConstants.notFoundStatusCode = 404;
AthenaeumConstants.internalServerErrorStatusCode = 500;
AthenaeumConstants.okStatusCode = 200;
AthenaeumConstants.imageFileTypes = ["image/png", "image/jpg", "image/jpeg", "image/tiff", "image/svg", "image/svg+png", "image/vnd.microsoft.icon", "image/gif"];
/**
 * 768px (bootstrap: $break-md)
 */
AthenaeumConstants.desktopMinWidth = 768;
AthenaeumConstants.newLineRegex = /\r\n|\\r\\n|\n\r|\n|\\n|\r|<br>|<br\/>/g;
AthenaeumConstants.markTagRegex = /<\s*mark[^>]*>(.*?)<\s*\/\s*mark>/ig;
AthenaeumConstants.smallTagRegex = /<\s*small[^>]*>(.*?)<\s*\/\s*small>/ig;
AthenaeumConstants.boldTagRegex = /<\s*b[^>]*>(.*?)<\s*\/\s*b>/ig;
AthenaeumConstants.underscoreTagRegex = /<\s*u[^>]*>(.*?)<\s*\/\s*u>/ig;
AthenaeumConstants.italicTagRegex = /<\s*i[^>]*>(.*?)<\s*\/\s*i>/ig;
AthenaeumConstants.dateRegex = /^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])T(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])(.[0-9]+)?(:([0-5][0-9]))?(Z)?$/;
AthenaeumConstants.zeroTimeRegex = /T00:00:00((.?0+)?)$/;
AthenaeumConstants.guidRegex = /^([0-9A-Fa-f]{8}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{12})$/;
AthenaeumConstants.defaultGuid = "00000000-0000-0000-0000-000000000000";
AthenaeumConstants.apiError = "__API__";
AthenaeumConstants.userNotDefinedError = `Component helper. User is not defined.`;

class GeoCoordinate {
  constructor(latitude = null, longitude = null) {
    this.lat = 0.0;
    this.lon = 0.0;
    this.isGeoCoordinate = true;
    this.lat = latitude || 0.0;
    this.lon = longitude || 0.0;
  }
}

class TimeSpan {
  /**
   * Initializes a new instance of the TimeSpan structure to a specified number of days, hours, minutes, seconds, and milliseconds.
   * @param days - Number of days.
   * @param hours - Number of hours.
   * @param minutes - Number of minutes.
   * @param seconds - Number of seconds.
   * @param milliseconds - Number of milliseconds.
   */
  constructor(days = 0, hours = 0, minutes = 0, seconds = 0, milliseconds = 0) {
    this.isTimeSpan = true;
    this._totalMilliseconds = (days * 3600 * 24 + hours * 3600 + minutes * 60 + seconds) * 1000 + milliseconds;
  }
  /**
   * Gets the value of the current TimeSpan structure expressed in whole and fractional milliseconds.
   */
  get totalMilliseconds() {
    return this._totalMilliseconds;
  }
  /**
   * Gets the value of the current TimeSpan structure expressed in whole and fractional seconds.
   */
  get totalSeconds() {
    return this._totalMilliseconds / TimeSpan.millisecondsPerSecond;
  }
  /**
   * Gets the value of the current TimeSpan structure expressed in whole and fractional minutes.
   */
  get totalMinutes() {
    return this._totalMilliseconds / TimeSpan.millisecondsPerMinute;
  }
  /**
   * Gets the value of the current TimeSpan structure expressed in whole and fractional hours.
   */
  get totalHours() {
    return this._totalMilliseconds / TimeSpan.millisecondsPerHour;
  }
  /**
   * Gets the value of the current TimeSpan structure expressed in whole and fractional days.
   */
  get totalDays() {
    return this._totalMilliseconds / TimeSpan.millisecondsPerDay;
  }
  /**
   * Gets the days component of the time interval represented by the current TimeSpan structure.
   */
  get days() {
    return Math.trunc(this.totalDays);
  }
  /**
   * Gets the hours component of the time interval represented by the current TimeSpan structure.
   */
  get hours() {
    return Math.trunc((this._totalMilliseconds - TimeSpan.millisecondsPerDay * this.days) / TimeSpan.millisecondsPerHour);
  }
  /**
   * Gets the minutes component of the time interval represented by the current TimeSpan structure.
   */
  get minutes() {
    return Math.trunc((this._totalMilliseconds - TimeSpan.millisecondsPerDay * this.days - TimeSpan.millisecondsPerHour * this.hours) / TimeSpan.millisecondsPerMinute);
  }
  /**
   * Gets the seconds component of the time interval represented by the current TimeSpan structure.
   */
  get seconds() {
    return Math.trunc((this._totalMilliseconds - TimeSpan.millisecondsPerDay * this.days - TimeSpan.millisecondsPerHour * this.hours - TimeSpan.millisecondsPerMinute * this.minutes) / TimeSpan.millisecondsPerSecond);
  }
  /**
   * Gets the milliseconds component of the time interval represented by the current TimeSpan structure.
   */
  get milliseconds() {
    return Math.trunc(this._totalMilliseconds - TimeSpan.millisecondsPerDay * this.days - TimeSpan.millisecondsPerHour * this.hours - TimeSpan.millisecondsPerMinute * this.minutes - TimeSpan.millisecondsPerSecond * this.seconds);
  }
  /**
   * Returns a new TimeSpan object whose value is the sum of the specified TimeSpan object and this instance.
   * @param ts - The time interval to add.
   * @returns TimeSpan - A new object that represents the value of this instance plus the value of ts.
   */
  add(ts) {
    const result = new TimeSpan();
    result._totalMilliseconds = this._totalMilliseconds + ts._totalMilliseconds;
    return result;
  }
  /**
   * Returns a new TimeSpan object whose value is the difference between the specified TimeSpan object and this instance.
   * @param ts - The time interval to be subtracted.
   * @returns TimeSpan - A new time interval whose value is the result of the value of this instance minus the value of ts.
   */
  subtract(ts) {
    const result = new TimeSpan();
    result._totalMilliseconds = this._totalMilliseconds - ts._totalMilliseconds;
    return result;
  }
  /**
   * Returns a new TimeSpan object which value is the result of multiplication of this instance and the specified factor.
   * @param factor - The value to be multiplied by.
   * @returns TimeSpan - A new object that represents the value of this instance multiplied by the value of factor.
   */
  multiply(factor) {
    const result = new TimeSpan();
    result._totalMilliseconds = this._totalMilliseconds * factor;
    return result;
  }
  /**
   * Returns a new TimeSpan object whose value is the negated value of this instance.
   * @returns TimeSpan - A new object with the same numeric value as this instance, but with the opposite sign.
   */
  negate() {
    const result = new TimeSpan();
    result._totalMilliseconds = -this._totalMilliseconds;
    return result;
  }
  toTimeString() {
    return `${Utility.pad(this.hours)}:${Utility.pad(this.minutes)}:${Utility.pad(this.seconds)}`;
  }
  toShortTimeString() {
    return `${Utility.pad(this.hours)}:${Utility.pad(this.minutes)}`;
  }
  /**
   * Returns a TimeSpan that represents a specified number of days, where the specification is accurate to the nearest millisecond.
   * @param days - A number of days, accurate to the nearest millisecond.
   * @returns TimeSpan - An object that represents value.
   */
  static fromDays(days) {
    return TimeSpan.fromMilliseconds(days * TimeSpan.millisecondsPerDay);
  }
  /**
   * Returns a TimeSpan that represents a specified number of hours, where the specification is accurate to the nearest millisecond.
   * @param hours - A number of hours accurate to the nearest millisecond.
   * @returns TimeSpan - An object that represents value.
   */
  static fromHours(hours) {
    return TimeSpan.fromMilliseconds(hours * TimeSpan.millisecondsPerHour);
  }
  /**
   * Returns a TimeSpan that represents a specified number of minutes, where the specification is accurate to the nearest millisecond.
   * @param minutes - A number of minutes, accurate to the nearest millisecond.
   * @returns TimeSpan - An object that represents value.
   */
  static fromMinutes(minutes) {
    return TimeSpan.fromMilliseconds(minutes * TimeSpan.millisecondsPerMinute);
  }
  /**
   * Returns a TimeSpan that represents a specified number of seconds, where the specification is accurate to the nearest millisecond.
   * @param seconds - A number of seconds, accurate to the nearest millisecond.
   * @returns TimeSpan - An object that represents value.
   */
  static fromSeconds(seconds) {
    return TimeSpan.fromMilliseconds(seconds * TimeSpan.millisecondsPerSecond);
  }
  /**
   * Returns a TimeSpan that represents a specified number of milliseconds.
   * @param milliseconds - A number of milliseconds.
   * @returns TimeSpan - An object that represents value.
   */
  static fromMilliseconds(milliseconds) {
    const result = new TimeSpan();
    result._totalMilliseconds = Math.round(milliseconds);
    return result;
  }
  /**
   * Compares this instance to a specified TimeSpan object and returns an integer that indicates whether this instance is shorter than, equal to, or longer than the TimeSpan object.
   * @param value - An object to compare to this instance.
   * @returns number - A signed number indicating the relative values of this instance and value.
   * "A negative integer": This instance is shorter than value.
   * "Zero": This instance is equal to value.
   * "A positive integer": This instance is longer than value.
   */
  compareTo(value) {
    if (this._totalMilliseconds > value._totalMilliseconds) {
      return 1;
    }
    if (this._totalMilliseconds < value._totalMilliseconds) {
      return -1;
    }
    return 0;
  }
  /**
   * Returns a hash code for this instance.
   * @returns number - A 32-bit signed integer hash code.
   */
  getHashCode() {
    // noinspection ShiftOutOfRangeJS
    return this._totalMilliseconds ^ this._totalMilliseconds >> 32;
  }
  /**
   * Returns a value indicating whether this instance is equal to a specified TimeSpan object.
   * @param obj - An object to compare with this instance.
   * @returns boolean - true if obj represents the same time interval as this instance; otherwise, false.
   */
  equals(obj) {
    return this._totalMilliseconds == obj._totalMilliseconds;
  }
  /**
   * Compares two TimeSpan values and returns an integer that indicates whether the first value is shorter than, equal to, or longer than the second value.
   * @param t1 - The first time interval to compare.
   * @param t2 - The second time interval to compare.
   * @returns number - One of the following values.
   * "-1": t1 is shorter than t2.
   * "0": t1 is equal to t2.
   * "1": t1 is longer than t2.
   */
  static compare(t1, t2) {
    return t1.compareTo(t2);
  }
  /**
   * Returns a value that indicates whether two specified instances of TimeSpan are equal.
   * @param t1 - The first time interval to compare.
   * @param t2 - The second time interval to compare.
   * @returns boolean - true if the values of t1 and t2 are equal; otherwise, false.
   */
  static equals(t1, t2) {
    return t1.equals(t2);
  }
}
TimeSpan.millisecondsPerSecond = 1000.0;
TimeSpan.millisecondsPerMinute = 60000.0;
TimeSpan.millisecondsPerHour = 3600000.0;
TimeSpan.millisecondsPerDay = 86400000.0;

class ObjectHashItem {
  constructor(object) {
    this.object = object;
    this.hash = null;
  }
}
class HashCodeUtility {
  static inc(hash, value) {
    return (hash << 5) - hash + value & 0xFFFFFFFF;
  }
  static object(object, cache) {
    if (object instanceof Date) {
      return this.getDateHashCode(object);
    }
    const index = cache.findIndex(item => item.object === object);
    if (index !== -1) {
      const hash = cache[index].hash;
      return hash !== null ? hash : index;
    }
    const cacheItem = new ObjectHashItem(object);
    cache.push(cacheItem);
    const hasOwnProperty = Object.prototype.hasOwnProperty;
    let hash = 0;
    const properties = [];
    for (let property in object) {
      if (hasOwnProperty.call(object, property)) {
        properties.push(property);
      }
    }
    const length = properties.length;
    if (length > 0) {
      properties.sort();
      for (let i = 0; i < length; i++) {
        const property = properties[i];
        hash = this.inc(hash, this.getStringHashCode(property));
        hash = this.inc(hash, this.invokeGetHashCode(object[property], cache));
      }
    }
    // for (let property in object) {
    //     if (hasOwnProperty.call(object, property)) {
    //         //hash += HashCodeUtility.hash(property + HashCodeUtility.getHashCode(object[property]));
    //         hash = this.inc(hash, this.getStringHashCode(property));
    //         hash = this.inc(hash, this.invokeGetHashCode(object[property], cache));
    //     }
    // }
    cacheItem.hash = hash;
    return hash;
  }
  static invokeGetHashCode(value, cache) {
    if (value != null) {
      switch (typeof value) {
        case "string":
          return this.getStringHashCode(value);
        case "number":
          return this.getNumberHashCode(value);
        case "boolean":
          return this.getBooleanHashCode(value);
        case "object":
          return this.object(value, cache);
      }
    }
    return 0;
  }
  static getStringHashCode(value) {
    // return (value)
    //     ? value.split("").reduce(function(a,b){a=((a<<5)-a)+b.charCodeAt(0);return a&a},0)
    //     : 0;
    const length = value.length;
    let hash = 0;
    for (let i = 0; i < length; i++) {
      //hash = (((hash << 5) - hash) + string.charCodeAt(i)) & 0xFFFFFFFF;
      hash = this.inc(hash, value.charCodeAt(i));
    }
    return hash;
  }
  static getNumberHashCode(value) {
    return this.getStringHashCode(value.toString());
  }
  static getBooleanHashCode(value) {
    //return this.getStringHashCode(value.toString());
    return value ? 3569038 : 97196323;
  }
  static getDateHashCode(value) {
    return this.getNumberHashCode(value.valueOf());
  }
  /**
   * Serves as the default hash function.
   * @returns Number - A hash code for the current object.
   */
  static getHashCode(value) {
    const cache = [];
    return this.invokeGetHashCode(value, cache);
  }
  static isEqual(x, y) {
    return this.getHashCode(x) === this.getHashCode(y);
  }
}

class DateUtility {
  static compare(x, y, inverse = false) {
    x = typeof x === "string" ? new Date(x) : x;
    y = typeof y === "string" ? new Date(y) : y;
    const xValue = x.valueOf();
    const yValue = y.valueOf();
    if (xValue < yValue) {
      return inverse ? 1 : -1;
    }
    if (xValue > yValue) {
      return inverse ? -1 : 1;
    }
    return 0;
  }
  static equals(x, y) {
    x = typeof x === "string" ? new Date(x) : x;
    y = typeof y === "string" ? new Date(y) : y;
    const xValue = x.valueOf();
    const yValue = y.valueOf();
    return xValue === yValue;
  }
  static time(x) {
    x = typeof x === "string" ? new Date(x) : x;
    return Utility.diff(x, x.date());
  }
  static add(x, y) {
    x = typeof x === "string" ? new Date(x) : x;
    const totalMilliseconds = x.getTime() + y.totalMilliseconds;
    return new Date(totalMilliseconds);
  }
  /**
   * Returns a value indicating the specified date is in daylight saving time, summer time (DST) for the current locale
   */
  static isDst(x) {
    x = typeof x === "string" ? new Date(x) : x;
    const year = x.getFullYear();
    const jan = new Date(year, 0, 1);
    const jul = new Date(year, 6, 1);
    const dstOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
    const offset = x.getTimezoneOffset();
    return offset < dstOffset;
  }
}

const DateExtensions = function () {
  if (Date.prototype.format == null) {
    Date.prototype.format = function (format) {
      return Utility.formatValue(this, format);
    };
  }
  if (Date.prototype.date == null) {
    Date.prototype.date = function () {
      return Utility.getDateWithoutTime(this);
    };
  }
  // if (Date.prototype.time == null) {
  //     Date.prototype.time = function (): TimeSpan {
  //         return DateUtility.time(this);
  //     };
  // }
  if (Date.prototype.addMilliseconds == null) {
    Date.prototype.addMilliseconds = function (value) {
      return Utility.addMilliseconds(this, value);
    };
  }
  if (Date.prototype.addSeconds == null) {
    Date.prototype.addSeconds = function (value) {
      return Utility.addSeconds(this, value);
    };
  }
  if (Date.prototype.addMinutes == null) {
    Date.prototype.addMinutes = function (value) {
      return Utility.addMinutes(this, value);
    };
  }
  if (Date.prototype.addHours == null) {
    Date.prototype.addHours = function (value) {
      return Utility.addHours(this, value);
    };
  }
  if (Date.prototype.addDays == null) {
    Date.prototype.addDays = function (value) {
      return Utility.addDays(this, value);
    };
  }
  if (Date.prototype.addMonths == null) {
    Date.prototype.addMonths = function (value) {
      return Utility.addMonths(this, value);
    };
  }
  if (Date.prototype.addYears == null) {
    Date.prototype.addYears = function (value) {
      return Utility.addYears(this, value);
    };
  }
  // if (Date.prototype.add == null) {
  //     Date.prototype.add = function (value: TimeSpan): Date {
  //         return DateUtility.add(this, value);
  //     };
  // }
  if (Date.prototype.toUniversalTime == null) {
    Date.prototype.toUniversalTime = function () {
      return Utility.toUtc(this);
    };
  }
  if (Date.prototype.toLocalTime == null) {
    Date.prototype.toLocalTime = function () {
      return Utility.toLocal(this);
    };
  }
  if (Date.prototype.utcValueOf == null) {
    Date.prototype.utcValueOf = function () {
      return Utility.utcValueOf(this);
    };
  }
  if (Date.prototype.getHashCode == null) {
    Date.prototype.getHashCode = function () {
      return HashCodeUtility.getDateHashCode(this);
    };
  }
  if (Date.prototype.inFuture == null) {
    Date.prototype.inFuture = function (dateOnly = false) {
      return Utility.inFuture(this, dateOnly);
    };
  }
  if (Date.prototype.inPast == null) {
    Date.prototype.inPast = function (dateOnly = false) {
      return Utility.inPast(this, dateOnly);
    };
  }
  if (Date.prototype.isSunday == null) {
    Date.prototype.isSunday = function () {
      return Utility.isSunday(this);
    };
  }
  if (Date.prototype.isToday == null) {
    Date.prototype.isToday = function () {
      return Utility.isToday(this);
    };
  }
  if (Date.prototype.inInterval == null) {
    Date.prototype.inInterval = function (from, to, dateOnly) {
      return Utility.inInterval(this, from, to, dateOnly);
    };
  }
  if (Date.prototype.toISODateString == null) {
    Date.prototype.toISODateString = function () {
      return Utility.toISODateString(this);
    };
  }
  if (Date.prototype.toLongISOString == null) {
    Date.prototype.toLongISOString = function () {
      return Utility.toLongISOString(this);
    };
  }
  if (Date.prototype.getDaysInMonth == null) {
    Date.prototype.getDaysInMonth = function () {
      return Utility.getDaysInMonth(this);
    };
  }
  if (Date.prototype.compareTo == null) {
    Date.prototype.compareTo = function (value, inverse = false) {
      return DateUtility.compare(this, value, inverse);
    };
  }
  if (Date.prototype.equals == null) {
    Date.prototype.equals = function (value) {
      return DateUtility.equals(this, value);
    };
  }
  if (Date.prototype.isDst == null) {
    Date.prototype.equals = function () {
      return DateUtility.isDst(this);
    };
  }
};
DateExtensions();

class StringUtility {
  static compare(x, y, inverse = false) {
    if (x < y) {
      return inverse ? 1 : -1;
    }
    if (x > y) {
      return inverse ? -1 : 1;
    }
    return 0;
  }
  static toPascalCase(value) {
    //return  _.upperFirst(_.camelCase(value));
    if (/^[\p{L}\d]+$/iu.test(value)) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    return value.replace(/([\p{L}\d])([\p{L}\d]*)/giu, (_, left, right) => left.toUpperCase() + right.toLowerCase()).replace(/[^\p{L}\d]/giu, '');
  }
}

const StringExtensions = function () {
  if (String.prototype.format == null) {
    String.prototype.format = function (...args) {
      return Utility.format(this, ...args);
    };
  }
  if (String.prototype.getHashCode == null) {
    String.prototype.getHashCode = function () {
      return HashCodeUtility.getStringHashCode(this);
    };
  }
  if (String.prototype.toPascalCase == null) {
    String.prototype.toPascalCase = function () {
      return StringUtility.toPascalCase(this);
    };
  }
};
StringExtensions();

const NumberExtensions = function () {
  if (Number.prototype.format == null) {
    Number.prototype.format = function (format) {
      return Utility.formatValue(this, format);
    };
  }
  if (Number.prototype.getHashCode == null) {
    Number.prototype.getHashCode = function () {
      return HashCodeUtility.getNumberHashCode(this);
    };
  }
};
NumberExtensions();

/**
 * Implementation of {@link ITypeResolver}.
 */
class TypeResolver {
  /**
   * If the object contains an enumerable non-inherited property with a value "true" and a name starting with "isX", where X is any uppercase letter,
   * then return the substring of such propertys name after "is". Otherwise return an empty string.
   *
   * Example: with input {isValue: true} a string "Value" is returned.
   */
  static getIsName(value) {
    const hasOwnProperty = Object.prototype.hasOwnProperty;
    for (let propertyName in value) {
      if (hasOwnProperty.call(value, propertyName)) {
        if (propertyName.length > 2 && propertyName.startsWith("is")) {
          const propertyValue = value[propertyName];
          if (propertyValue === true) {
            const name = propertyName.substr(2);
            const prefix = name[0];
            if (prefix === prefix.toUpperCase()) {
              return name;
            }
          }
        }
      }
    }
    return "";
  }
  /**
   * @return The string "type:" appended with a hash calculated from the objects enumerable non-inherited properties.
   */
  static getObjectTypeHashCode(value) {
    const hasOwnProperty = Object.prototype.hasOwnProperty;
    const properties = [];
    for (let property in value) {
      if (hasOwnProperty.call(value, property)) {
        properties.push(property);
      }
    }
    const hashCode = Utility.getHashCode(properties);
    return `type:${hashCode}`;
  }
  /**
   * @return Do the values have equal {@link ServiceType}'s.
   */
  is(value, type) {
    return value != null ? this.resolve(value) === this.resolve(type) : false;
  }
  // ITypeResolver
  resolve(value) {
    switch (typeof value) {
      case "string":
        return "string";
      case "boolean":
        return "boolean";
      case "number":
        return "number";
      case "function":
        return value.name;
      case "object":
        const service = value;
        if (typeof service.getType === "function") {
          return service.getType();
        }
        let name = value.constructor.name;
        if (name !== "Object") {
          return name;
        }
        name = TypeResolver.getIsName(value);
        if (name) {
          return name;
        }
        return TypeResolver.getObjectTypeHashCode(value);
      default:
        return value;
    }
  }
  // IService
  getType() {
    return "ITypeResolver";
  }
}
var TypeResolver$1 = new TypeResolver();

class Singleton {
  constructor() {
    this._data = null;
  }
  getData() {
    if (this._data == null) {
      const container = window;
      this._data = container.__reApptorSingletonData || (container.__reApptorSingletonData = new Dictionary());
    }
    return this._data;
  }
  get(name, instance, warning = true) {
    const data = this.getData();
    if (data.containsKey(name)) {
      if (warning) {
        console.warn(`ReApptor Singleton Warning. Multiple instance of singleton object "${name}" found. Probably several instances of one of the packages are loaded. This will not break the app but it is not recommended to use two major versions.`);
      }
      return data.getValue(name);
    }
    const value = typeof instance === "function" ? instance() : instance;
    data.setValue(name, value);
    return value;
  }
}
var Singleton$1 = new Singleton();

class ServiceProvider {
  constructor() {
    this._services = new Dictionary();
  }
  set(serviceType, service) {
    this._services.setValue(serviceType, service);
  }
  get(serviceType) {
    return this._services.getValue(serviceType);
  }
  /**
   * Resolves service type.
   * @param serviceOrType - A service declaration, service type or service instance.
   * @returns ServiceType - A service type.
   */
  resolveServiceType(serviceOrType) {
    const typeResolver = this.getTypeResolver();
    return typeof serviceOrType === "string" ? serviceOrType : typeResolver.resolve(serviceOrType);
  }
  /**
   * Gets the service object of the specified type.
   * @param serviceOrType - A service declaration, service type or service instance.
   * @param resolve - if True then the service callback will be automatically resolved (invoked); if False, then the function will be returned.
   * @returns IService | object | null - A service object of type serviceType or null if there is no service object of type serviceType.
   */
  getService(serviceOrType, resolve = true) {
    const serviceType = this.resolveServiceType(serviceOrType);
    const service = this.get(serviceType);
    return service != null ? resolve && typeof service === "function" ? service(serviceType) : service : null;
  }
  /**
   * Get service of type serviceType from the IServiceProvider.
   * @param serviceOrType - A service declaration, service type or service instance.
   * @param resolve - if True then the service callback will be automatically resolved (invoked); if False, then the function will be returned.
   * @returns A service object of type serviceType.
   * @exception InvalidOperationException There is no service of type serviceType.
   */
  getRequiredService(serviceOrType, resolve = true) {
    const serviceType = this.resolveServiceType(serviceOrType);
    const service = this.getService(serviceType, resolve);
    if (service == null) throw new Error(`InvalidOperationException. There is no service of type "${serviceType}".`);
    return service;
  }
  /**
   * Adds a singleton service of the type specified in serviceType.
   * @param serviceOrType - A service declaration, service type or service instance.
   * @param service - a service instance if service type is specified in first argument
   */
  addSingleton(serviceOrType, service = null) {
    const serviceType = this.resolveServiceType(serviceOrType);
    service = service != null ? service : serviceOrType;
    this.set(serviceType, service);
  }
  /**
   * @return An {@link ITypeResolver}.
   */
  getTypeResolver() {
    const serviceType = "ITypeResolver";
    const service = this.get(serviceType);
    if (service != null) {
      return service;
    }
    this.set(serviceType, TypeResolver$1);
    return TypeResolver$1;
  }
  /**
   * @return An {@link ILocalizer}, or null if no {@link ILocalizer} service is registered.
   */
  findLocalizer() {
    return this.getService("ILocalizer");
  }
  /**
   * @return An {@link ILocalizer}.
   * @throws No {@link ILocalizer} service is registered.
   */
  getLocalizer() {
    return this.getRequiredService("ILocalizer");
  }
  /**
   * @return An {@link IEnumProvider}, or null if no {@link IEnumProvider} service is registered.
   */
  findEnumProvider() {
    return this.getService("IEnumProvider");
  }
  /**
   * @return An {@link ITransformProvider}, or null if no {@link ITransformProvider} service is registered.
   */
  findTransformProvider() {
    return this.getService("ITransformProvider");
  }
  // IService
  getType() {
    return "ServiceProvider";
  }
}
var ServiceProvider$1 = Singleton$1.get("ServiceProvider", new ServiceProvider());

var SortDirection;
(function (SortDirection) {
  SortDirection[SortDirection["Asc"] = 0] = "Asc";
  SortDirection[SortDirection["Desc"] = 1] = "Desc";
})(SortDirection || (SortDirection = {}));
class Utility {
  static setTimeout(asyncCallback, ms, ...args) {
    const timer = setTimeout((...args) => asyncCallback(args), ms, args);
    return timer;
  }
  static wait(ms) {
    return new Promise(resolve => this.setTimeout(resolve, ms));
  }
  static background(action) {
    setTimeout(action, 0);
  }
  static get geoEnabled() {
    return this._geoEnabled || (this._geoEnabled = !!navigator.geolocation);
  }
  static getPositionAsync(options = null) {
    return __awaiter(this, void 0, void 0, function* () {
      if (this.geoEnabled) {
        options = options || {
          maximumAge: 30000,
          timeout: 1000
        };
        options.timeout = options.timeout || 1000;
        const stopWatchTimeout = 1.1 * options.timeout;
        let stopWatchTimer = null;
        const clearStopWatchTimer = () => {
          if (stopWatchTimer) {
            clearTimeout(stopWatchTimer);
            stopWatchTimer = null;
          }
        };
        return new Promise((resolve, reject) => {
          stopWatchTimer = setTimeout(() => {
            resolve(null);
          }, stopWatchTimeout);
          navigator.geolocation.getCurrentPosition(resolve, reject, options || undefined);
        }).then(position => {
          clearStopWatchTimer();
          return position;
        }).catch(e => {
          clearStopWatchTimer();
          const timeout = e.code == 3;
          if (!timeout) {
            this._geoEnabled = false;
          }
          return null;
        });
      } else {
        return new Promise(resolve => resolve(null));
      }
    });
  }
  static getLocationAsync(options = null) {
    return __awaiter(this, void 0, void 0, function* () {
      const position = yield this.getPositionAsync(options);
      if (position) {
        return new GeoCoordinate(position.coords.latitude, position.coords.longitude);
      }
      return null;
    });
  }
  static css(...params) {
    return params ? params.map(param => typeof param === "function" ? param() : param).filter(param => param).join(" ").replace(",", " ").trim() : "";
  }
  /**
   * Format a string.
   * "D" = dd.MM.yyyy
   *
   * @param text String to format.
   * @param params Values used to format the string.
   * @return The input string formatted with the parameters. If the input string is not defined, an empty string.
   */
  static format(text, ...params) {
    let result = text || "";
    if (result && params != null && params.length > 0) {
      params.forEach((param, index) => {
        const prefix = `{${index}`;
        let i = result.indexOf(prefix);
        while (i !== -1) {
          const customFormat = result[i + 2] === ":" && (typeof param === "number" || typeof param === "object" || typeof param === "string");
          if (customFormat) {
            if (param != null && (typeof param === "number" || typeof param === "string" || typeof param === "object" && (param.constructor.name === Date.name || param instanceof TimeSpan || param.isTimeSpan))) {
              let j = result.indexOf("}", i + 2);
              if (j !== -1) {
                let format = result.substring(i + prefix.length + 1, j);
                if (format) {
                  let formattedParam = null;
                  if (typeof param === "number") {
                    const enumProvider = ServiceProvider$1.findEnumProvider();
                    //number
                    if (format === "c" || format === "C") {
                      formattedParam = this.toCurrencyString(param);
                    } else if (format === "0" || format === "n" || format === "N") {
                      formattedParam = param.toFixed(0).toString();
                    } else if (format === "00") {
                      formattedParam = Utility.pad(param, false);
                    } else if (format === "000") {
                      formattedParam = Utility.pad(param, false, 3);
                    } else if (format === "0000") {
                      formattedParam = Utility.pad(param, false, 4);
                    } else if (format === "0.0") {
                      formattedParam = param.toFixed(1).toString();
                    } else if (format === "# ##0.0") {
                      formattedParam = this.addThousandsSeparator(param.toFixed(1).toString());
                    } else if (format === "# ##0") {
                      formattedParam = this.addThousandsSeparator(param.toFixed(0).toString());
                    } else if (format === "0.00") {
                      formattedParam = param.toFixed(2).toString();
                    } else if (format === "# ##0.00") {
                      formattedParam = this.addThousandsSeparator(param.toFixed(2).toString());
                    } else if (format === "%" || format === "0%") {
                      formattedParam = Math.round(param) + "%";
                    } else if (format === "0.0%") {
                      formattedParam = param.toFixed(1) + "%";
                    } else if (format === "0.00%") {
                      formattedParam = param.toFixed(2) + "%";
                    } else if (format === "lat" || format === "lon") {
                      formattedParam = param.toFixed(8);
                    } else if (enumProvider && enumProvider.isEnum(format)) {
                      formattedParam = enumProvider.getEnumText(format, param);
                    } else if (enumProvider && format.toLowerCase().endsWith(".description") && enumProvider.isEnum(format.substring(0, format.length - "description".length - 1))) {
                      formattedParam = enumProvider.getEnumDescription(format.substring(0, format.length - "description".length - 1), param);
                    }
                  } else if (typeof param === "string" || typeof param === "object" && param.constructor.name === Date.name) {
                    if (format === "dddd") {
                      //The abbreviated name of the day of the week.
                      formattedParam = this.getDayOfWeek(param);
                    } else if (format === "ddd") {
                      //The abbreviated name of the day of the week.
                      formattedParam = this.getShortDayOfWeek(param);
                    } else if (format === "MMMM") {
                      //The name of the month.
                      formattedParam = this.getMonth(param);
                    } else if (format === "MMM") {
                      //The abbreviated name of the month.
                      formattedParam = this.getShortMonth(param);
                    } else if (format === "D" || format === "dd.MM.yyyy") {
                      const date = new Date(param);
                      formattedParam = this.toDateString(date);
                    } else if (format === "G") {
                      const date = new Date(param);
                      formattedParam = this.toDateTimeString(date);
                    } else if (format === "g") {
                      const date = new Date(param);
                      formattedParam = this.toDateShortTimeString(date);
                    } else if (format === "t" || format == "HH:mm") {
                      const date = new Date(param);
                      formattedParam = this.toShortTimeString(date);
                    } else if (format === "HH:mm:ss") {
                      const date = new Date(param);
                      formattedParam = this.toTimeString(date);
                    } else if (format === "MM.yyyy") {
                      const date = new Date(param);
                      formattedParam = `${Utility.pad(date.getMonth() + 1)}.${date.getFullYear()}`;
                    } else if (format === "dd.MM") {
                      const date = new Date(param);
                      formattedParam = `${Utility.pad(date.getDate())}.${Utility.pad(date.getMonth() + 1)}`;
                    } else if (format === "d" || format === "dd.MM.yy") {
                      const date = new Date(param);
                      formattedParam = this.toShortDateString(date);
                    } else if (format === "dt" || format === "dd.MM.yy HH:mm") {
                      const date = new Date(param);
                      formattedParam = this.toShortDateShortTimeString(date);
                    }
                  } else if (typeof param === "object" && (param instanceof TimeSpan || param.isTimeSpan === true)) {
                    const value = param;
                    if (format === "hh:mm:ss") {
                      formattedParam = `${Utility.pad(value.hours)}:${Utility.pad(value.minutes)}:${Utility.pad(value.seconds)}`;
                    } else if (format === "c") {
                      formattedParam = value.days > 0 ? `${value.days}.${Utility.pad(value.hours)}:${Utility.pad(value.minutes)}:${Utility.pad(value.seconds)}` : `${Utility.pad(value.hours)}:${Utility.pad(value.minutes)}:${Utility.pad(value.seconds)}`;
                    }
                  }
                  if (formattedParam != null) {
                    result = result.substring(0, i) + formattedParam + result.substring(j + 1);
                  }
                }
              }
            }
          } else {
            let str = "";
            if (param != null) {
              const transformProvider = ServiceProvider$1.findTransformProvider();
              str = transformProvider ? transformProvider.toString(param) : param.toString();
            }
            result = result.replace(`{${index}}`, str);
          }
          i = result.indexOf(prefix, i + 1);
        }
      });
    }
    return result;
  }
  static formatValue(value, format = null) {
    return value != null ? format ? typeof format === "function" ? format(value) : Utility.format(`{0:${format}}`, value) : Utility.format(`{0}`, value) : "";
  }
  static getDayOfWeek(dayOfWeekOrDate) {
    switch (typeof dayOfWeekOrDate) {
      case "string":
        dayOfWeekOrDate = new Date(dayOfWeekOrDate);
        return this.getDayOfWeek(dayOfWeekOrDate);
      case "number":
        let name;
        switch (dayOfWeekOrDate) {
          case 0:
            name = "Sunday";
            break;
          case 1:
            name = "Monday";
            break;
          case 2:
            name = "Tuesday";
            break;
          case 3:
            name = "Wednesday";
            break;
          case 4:
            name = "Thursday";
            break;
          case 5:
            name = "Friday";
            break;
          case 6:
            name = "Saturday";
            break;
          default:
            throw Error(`Unsupported day of week number "${dayOfWeekOrDate}", can be [0..6] => [Sunday..Saturday].`);
        }
        let localizer = ServiceProvider$1.findLocalizer();
        let language = navigator.language;
        if (localizer) {
          const tag = `DayOfWeek.${name}`;
          if (localizer.contains(tag)) {
            return localizer.get(tag);
          }
          language = localizer.language;
        }
        const sunday = new Date(Date.UTC(2017, 0, 1));
        const dayOfWeek = sunday.addDays(dayOfWeekOrDate);
        name = dayOfWeek.toLocaleString(language, {
          weekday: "long"
        });
        return name;
      case "object":
        if (typeof dayOfWeekOrDate.getDay === "function") {
          dayOfWeekOrDate = dayOfWeekOrDate.getDay();
          return this.getDayOfWeek(dayOfWeekOrDate);
        }
        break;
    }
    throw Error(`Unsupported type for day of week "${dayOfWeekOrDate}", can be number, string or Date.`);
  }
  static getShortDayOfWeek(dayOfWeekOrDate) {
    const dayOfWeek = this.getDayOfWeek(dayOfWeekOrDate);
    return dayOfWeek.substr(0, 2);
  }
  static getShortMonth(monthOrDate) {
    const month = this.getMonth(monthOrDate);
    return month.substr(0, 3);
  }
  static getMonth(monthOrDate) {
    if (Utility.isDateType(monthOrDate)) {
      monthOrDate = monthOrDate.getMonth();
      return this.getMonth(monthOrDate);
    }
    if (typeof monthOrDate === "string") {
      monthOrDate = monthOrDate.toLowerCase();
    }
    const localizer = ServiceProvider$1.findLocalizer();
    switch (monthOrDate) {
      case "january":
      case 0:
        return localizer ? localizer.get("Month.January") : "January";
      case "february":
      case 1:
        return localizer ? localizer.get("Month.February") : "February";
      case "march":
      case 2:
        return localizer ? localizer.get("Month.March") : "March";
      case "april":
      case 3:
        return localizer ? localizer.get("Month.April") : "April";
      case "may":
      case 4:
        return localizer ? localizer.get("Month.May") : "May";
      case "june":
      case 5:
        return localizer ? localizer.get("Month.June") : "June";
      case "july":
      case 6:
        return localizer ? localizer.get("Month.July") : "July";
      case "august":
      case 7:
        return localizer ? localizer.get("Month.August") : "August";
      case "september":
      case 8:
        return localizer ? localizer.get("Month.September") : "September";
      case "october":
      case 9:
        return localizer ? localizer.get("Month.October") : "October";
      case "november":
      case 10:
        return localizer ? localizer.get("Month.November") : "November";
      case "december":
      case 11:
        return localizer ? localizer.get("Month.December") : "December";
    }
    throw Error(`Unsupported month "${monthOrDate}", can be number ([0..11]), string (month name in English) or Date.`);
  }
  static toCurrencyString(input) {
    const value = input.toFixed(2);
    return this.addThousandsSeparator(value);
  }
  static addThousandsSeparator(input) {
    return input.replace(/\d(?=(\d{3})+\.?$)/g, '$& ');
  }
  static addMilliseconds(date, milliseconds) {
    const copy = new Date(date);
    copy.setMilliseconds(copy.getMilliseconds() + milliseconds);
    return copy;
  }
  static addSeconds(date, seconds) {
    const copy = new Date(date);
    copy.setSeconds(copy.getSeconds() + seconds);
    return copy;
  }
  static addMinutes(date, minutes) {
    const copy = new Date(date);
    //copy.setTime(copy.getTime() + (minutes*60*1000));
    copy.setMinutes(copy.getMinutes() + minutes);
    return copy;
  }
  static addHours(date, hours) {
    const copy = new Date(date);
    //copy.setTime(copy.getTime() + (hours*60*60*1000));
    copy.setHours(copy.getHours() + hours);
    return copy;
  }
  static addDays(date, days) {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + days);
    return copy;
  }
  static addMonths(date, months) {
    date = new Date(date);
    const day = date.getDate();
    date.setDate(1);
    date.setMonth(date.getMonth() + months);
    date.setDate(Math.min(day, this.getDaysInMonth(date)));
    return date;
  }
  static addYears(date, years) {
    date = new Date(date);
    date.setFullYear(date.getFullYear() + years);
    return date;
  }
  static isLeapYear(year = null) {
    return year != null ? year % 4 === 0 && year % 100 !== 0 || year % 400 === 0 : this.isLeapYear(this.now().getFullYear());
  }
  static getDaysInMonth(date) {
    date = new Date(date);
    const year = date.getFullYear();
    const month = date.getMonth();
    return [31, this.isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
  }
  static digits(value) {
    value = Math.abs(value);
    value = Math.trunc(value);
    return value.toString().length;
  }
  static round(value, digits = 0) {
    digits = Math.round(digits);
    if (digits > 0) {
      const intDigits = this.digits(value);
      const totalDigits = intDigits + digits;
      const maxDigits = 18;
      if (totalDigits > maxDigits) {
        digits -= totalDigits - maxDigits;
        if (digits <= 0) {
          return Math.round(value);
        }
      }
      const k = Math.pow(10, digits);
      value *= k;
      value = Math.round(value);
      value /= k;
    }
    return value;
  }
  static roundE(value) {
    return this.round(value, 10);
  }
  static roundHalf(value) {
    return Math.round(value * 2) / 2;
  }
  /**
   * @param value Number to create a padded string from.
   * @param abs If true value will be abs
   * @param length Length of the result string
   * @return The number as a string, with an added leading zero if the number is smaller than 10.
   */
  static pad(value, abs = true, length = 2) {
    value = Math.floor(value);
    if (abs) {
      value = Math.abs(value);
    }
    let str = value.toString();
    while (str.length < length) {
      str = "0" + str;
    }
    return str;
  }
  /**
   * @return A new {@link Date} with the browsers current date, time and time-zone.
   */
  static now() {
    return new Date();
  }
  /**
   * @return A new {@link Date} with the browsers current date, time and time-zone, with its time-component adjusted to look like it is in UTC time.
   * @see toUtc
   */
  static utcNow() {
    const now = Utility.now();
    return Utility.toUtc(now);
  }
  /**
   * @see date
   */
  static today() {
    return Utility.date();
  }
  static tomorrow() {
    return this.today().addDays(1);
  }
  static getWeekNumber(date) {
    const beginningOfTheYear = new Date(date.getFullYear(), 0, 1);
    const days = Math.floor((date.getTime() - beginningOfTheYear.getTime()) / (24 * 60 * 60 * 1000));
    return Math.ceil(days / 7);
  }
  /**
   * A new {@link Date} with the browsers current date and time-zone, with its time-zone specific time-component set to 0.
   * @see getDateWithoutTime
   */
  static date() {
    return this.getDateWithoutTime(this.now());
  }
  static inInterval(date, from, to, dateOnly = false) {
    const dateValue = dateOnly ? new Date(date).date().valueOf() : new Date(date).valueOf();
    const fromValue = from != null ? dateOnly ? new Date(from).date().valueOf() : new Date(from).valueOf() : 0;
    const toValue = to != null ? dateOnly ? new Date(to).date().valueOf() : new Date(to).valueOf() : Number.MAX_VALUE;
    return fromValue <= dateValue && dateValue <= toValue;
  }
  static inFuture(date, dateOnly = false) {
    return date != null && Utility.diff(date, dateOnly ? Utility.today() : Utility.now()).totalMilliseconds > 0;
  }
  static inPast(date, dateOnly = false) {
    return date != null && Utility.diff(dateOnly ? Utility.today() : Utility.now(), date).totalMilliseconds > 0;
  }
  /**
   * NOTE: The output {@link Date} is still in the browsers time-zone!
   *
   * @return A new {@link Date} with its time-component adjusted to look like it is in UTC time.
   */
  static toUtc(date) {
    date = new Date(date);
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());
  }
  /**
   * Create a copy of the input {@link Date} and add the {@link timezoneOffset} to it.
   * This makes the output {@link Date}'s internal UTC date- and time-components equal to the input {@link Date}'s timezone-specific ones.
   * Extremely useful when making sure {@link Date}'s are serialized properly.
   *
   * Example:
   *      Browsers timezone is UTC+2.
   *      Input {@link Date} is 2000-01-01-00-00-00, and thus the output {@link Date} is 2000-01-01-02-00-00.
   *      The input date would be serialized as "1999-12-31T22:00:00.000Z", whereas the output date would be serialized as "2000-01-01T00:00:00.000Z".
   *
   * @see utcValueOf
   */
  static asUtc(date) {
    return new Date(Utility.utcValueOf(date));
  }
  static toLocal(date) {
    date = new Date(date);
    const hoursDiff = date.getHours() - date.getTimezoneOffset() / 60;
    date.setHours(hoursDiff);
    return date;
  }
  static utcValueOf(date) {
    if (typeof date === "string") {
      date = new Date(date);
    }
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  }
  static isSunday(date) {
    const value = typeof date === "string" ? new Date(date) : date;
    return value.getDay() == 0;
  }
  static isToday(date) {
    const value = typeof date === "string" ? new Date(date) : date;
    const today = this.today();
    return value.getDate() === today.getDate() && value.getMonth() === today.getMonth() && value.getFullYear() === today.getFullYear();
  }
  /**
   * @return The input {@link Date}'s date-component in ISO 8601 format (yyyy-mm-dd)
   */
  static toISODateString(date) {
    return date.getFullYear() + "-" + Utility.pad(date.getMonth() + 1) + "-" + Utility.pad(date.getDate());
  }
  /**
   * @return The input {@link Date}'s date-component, time-component and timezone in ISO 8601 format (yyyy-mm-ddThh:MM:ss+Z:Z)
   */
  static toLongISOString(date) {
    const tzo = -date.getTimezoneOffset();
    const dif = tzo >= 0 ? "+" : "-";
    return date.getFullYear() + "-" + Utility.pad(date.getMonth() + 1) + "-" + Utility.pad(date.getDate()) + "T" + Utility.pad(date.getHours()) + ":" + Utility.pad(date.getMinutes()) + ":" + Utility.pad(date.getSeconds()) + dif + Utility.pad(tzo / 60) + ":" + Utility.pad(tzo % 60);
  }
  static toDateTimeString(date) {
    return `${Utility.toDateString(date)} ${Utility.toTimeString(date)}`;
  }
  static toShortDateString(date) {
    const value = typeof date === "string" ? new Date(date) : date;
    let year = value.getFullYear().toString().substring(2);
    year = year.length == 1 ? "0" + year : year;
    return `${Utility.pad(value.getDate())}.${Utility.pad(value.getMonth() + 1)}.${year}`;
  }
  static toTimeString(date) {
    const value = typeof date === "string" ? new Date(date) : date;
    return `${Utility.pad(value.getHours())}:${Utility.pad(value.getMinutes())}:${Utility.pad(value.getSeconds())}`;
  }
  static toDateShortTimeString(date) {
    const value = typeof date === "string" ? new Date(date) : date;
    return this.toDateString(value) + " " + this.toShortTimeString(value);
  }
  static toShortDateShortTimeString(date) {
    const value = typeof date === "string" ? new Date(date) : date;
    return `${this.toShortDateString(value)} ${this.toShortTimeString(value)}`;
  }
  static toDateString(date) {
    const value = typeof date === "string" ? new Date(date) : date;
    return `${Utility.pad(value.getDate())}.${Utility.pad(value.getMonth() + 1)}.${Utility.pad(value.getFullYear())}`;
  }
  static toShortTimeString(date) {
    return `${Utility.pad(date.getHours())}:${Utility.pad(date.getMinutes())}`;
  }
  /**
   * @return Copy of the input {@link Date} with its timezone-specific time-component set to 0.
   */
  static getDateWithoutTime(date) {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
  }
  /**
   * Difference in minutes between the browsers time-zone and UTC.
   */
  static get timezoneOffset() {
    const timezoneOffset = new Date().getTimezoneOffset();
    return -timezoneOffset;
  }
  static diff(x, y, ignoreTimeZone = false) {
    if (typeof x === "string") {
      x = new Date(x);
    }
    if (typeof y === "string") {
      y = new Date(y);
    }
    const xDate = ignoreTimeZone ? x.utcValueOf() : x.valueOf();
    const yDate = ignoreTimeZone ? y.utcValueOf() : y.valueOf();
    const mlsec = xDate - yDate;
    return TimeSpan.fromMilliseconds(mlsec);
  }
  /**
   * Checks if global click happened outside of component
   * @param target - target of click event
   * @param id - id of the component
   * @param exceptId - id of the exception component to not trigger outside click (default null)
   * @param exceptTag - string value of HTML tag that is inside excepted container, but triggers outside click (for case when there are multiple children inside excepted container
   * @returns boolean - true/false if global click happened outside of the component
   */
  static clickedOutside(target, id, exceptId = null, exceptTag = null) {
    const container = document.querySelector(`#${id}`);
    let outside = container !== null && !container.contains(target);
    if (outside && exceptId) {
      const exceptedContainer = document.querySelector(`#${exceptId}`);
      if (exceptedContainer !== null) {
        outside = !exceptedContainer.contains(target);
      }
      if (exceptTag && exceptedContainer) {
        const exceptedFound = exceptedContainer.getElementsByTagName(exceptTag)[0];
        if (exceptedFound && target === exceptedFound) {
          outside = true;
        }
      }
    }
    return outside;
  }
  static readUploadedFileAsDataUrl(file) {
    return __awaiter(this, void 0, void 0, function* () {
      const reader = new FileReader();
      return new Promise((resolve, reject) => {
        reader.onerror = () => {
          reader.abort();
          reject(new DOMException("Problem parsing input file."));
        };
        reader.onload = () => {
          const result = reader.result || "";
          resolve(result);
        };
        reader.readAsDataURL(file);
      });
    });
  }
  static readUploadedFileAsBinaryString(file) {
    return __awaiter(this, void 0, void 0, function* () {
      const fileReader = new FileReader();
      return new Promise((resolve, reject) => {
        fileReader.onerror = () => {
          fileReader.abort();
          reject(new DOMException("Problem parsing input file."));
        };
        fileReader.onload = () => {
          const result = fileReader.result || "";
          resolve(result);
        };
        fileReader.readAsBinaryString(file);
      });
    });
  }
  static readUploadedFileAsArrayBuffer(inputFile) {
    return __awaiter(this, void 0, void 0, function* () {
      const fileReader = new FileReader();
      return new Promise((resolve, reject) => {
        fileReader.onerror = () => {
          fileReader.abort();
          reject(new DOMException("Problem parsing input file."));
        };
        fileReader.onload = () => {
          resolve(fileReader.result);
        };
        fileReader.readAsArrayBuffer(inputFile);
      });
    });
  }
  static getFileExtension(name) {
    if (name) {
      const index = name.lastIndexOf(".");
      if (index !== -1 && index < name.length) {
        return name.substr(index);
      }
    }
    return "";
  }
  static getFileTypeName(fileOrFileType) {
    if (typeof fileOrFileType === "object") {
      return Utility.getFileTypeName(fileOrFileType.type);
    }
    return fileOrFileType.trim().split(/\/|\.+/).last().split(/\++/).first().replace("icon", "ico");
  }
  static getMimeType(fileType) {
    const fileTypeName = Utility.getFileTypeName(fileType);
    const mimeType = AthenaeumConstants.imageFileTypes.firstOrDefault(mimeType => Utility.getFileTypeName(mimeType) == fileTypeName);
    return mimeType || fileType;
  }
  static toBlob(fileOrSrc, sliceSize = 512) {
    let src;
    let type;
    if (typeof fileOrSrc === "string") {
      src = fileOrSrc;
      type = "";
    } else {
      src = fileOrSrc.src;
      type = fileOrSrc.type;
    }
    src = this.cutDataPrefixFromSrc(src);
    const byteCharacters = atob(src);
    const byteArrays = [];
    const length = byteCharacters.length;
    for (let offset = 0; offset < length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const sliceLength = slice.length;
      const byteNumbers = new Array(sliceLength);
      for (let i = 0; i < sliceLength; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, {
      type: type
    });
  }
  static cutDataPrefixFromSrc(src) {
    const srcPrefix = src.length > 100 ? src.substring(0, 99) : src;
    const base64Prefix = "base64,";
    const base64Index = srcPrefix.indexOf(base64Prefix);
    if (base64Index != -1) {
      src = src.substring(base64Index + base64Prefix.length);
    }
    return src;
  }
  static toObjectUrl(fileOrSrc, sliceSize = 512) {
    const blob = Utility.toBlob(fileOrSrc, sliceSize);
    return URL.createObjectURL(blob);
  }
  static isBase64(value) {
    try {
      value = this.cutDataPrefixFromSrc(value);
      window.atob(value);
      return true;
    } catch (_a) {
      return false;
    }
  }
  /**
   * @param mimeType mimeType to update
   * @return if mimeType is known then it will return the new version otherwise it will return the given mimeType
   */
  static overwriteDeprecatedMimeTypes(mimeType) {
    if (mimeType === "application/font-woff") {
      return "font/woff";
    }
    return mimeType;
  }
  /**
   * some browsers returns some deprecated mimeTypes as part of DataUrl, this method will try to update the known ones.
   * @param dataUrl output of fileReader {@see readUploadedFileAsDataUrl}
   * @param fileExtension file extension with dot {@see getFileExtension}
   */
  static overwriteDeprecatedMimeTypeInDataUrl(dataUrl, fileExtension) {
    if (fileExtension === ".woff" && dataUrl.startsWith("data:application/font-woff")) {
      return dataUrl.replace("data:application/font-woff", "data:font/woff");
    }
    if (fileExtension === ".woff" && dataUrl.startsWith("data:application/octet-stream")) {
      return dataUrl.replace("data:application/octet-stream", "data:font/woff");
    }
    if (fileExtension === ".woff2" && dataUrl.startsWith("data:application/octet-stream")) {
      return dataUrl.replace("data:application/octet-stream", "data:font/woff2");
    }
    return dataUrl;
  }
  /**
   * some browsers don't return mimeType for some file extension so this method will try to return the correct mimeType.
   * @param fileExtension file extension with dot {@see getFileExtension}
   */
  static getKnownMimeTypesByFileExtension(fileExtension) {
    if (fileExtension === ".woff") {
      return "font/woff";
    }
    if (fileExtension === ".woff2") {
      return "font/woff2";
    }
    if (fileExtension === ".ttf") {
      return "font/ttf";
    }
    if (fileExtension === ".otf") {
      return "font/otf";
    }
    return null;
  }
  static transformFileAsync(fileReference) {
    return __awaiter(this, void 0, void 0, function* () {
      if (fileReference) {
        const dataUrl = yield Utility.readUploadedFileAsDataUrl(fileReference);
        if (dataUrl) {
          const fileExtension = this.getFileExtension(fileReference.name);
          const checkedDataUrl = Utility.overwriteDeprecatedMimeTypeInDataUrl(dataUrl, fileExtension);
          const checkedMimeType = Utility.overwriteDeprecatedMimeTypes(fileReference.type);
          const file = new FileModel();
          file.lastModified = new Date(fileReference.lastModified);
          file.name = fileReference.name;
          file.size = fileReference.size;
          file.type = checkedMimeType || Utility.getKnownMimeTypesByFileExtension(fileExtension) || fileExtension;
          file.src = checkedDataUrl;
          return file;
        }
      }
      return null;
    });
  }
  static base64FromDataUrl(dataUrl) {
    return dataUrl.split("base64,")[1];
  }
  static findInstanceByAccessor(instance, accessor) {
    if (instance == null || accessor == null || typeof instance !== "object") {
      return undefined;
    }
    accessor = accessor.replace(/\[(\w+)]/g, '.$1'); // convert indexes to properties
    accessor = accessor.replace(/^\./, ''); // strip a leading dot
    const path = accessor.split('.');
    const length = path.length;
    let tuple;
    for (let i = 0, n = length; i < n; ++i) {
      const key = path[i];
      if (instance == null || !(key in instance)) {
        return undefined;
      }
      tuple = [instance, key];
      instance = instance[key];
      if (instance == null && i === n) {
        return undefined;
      }
    }
    return tuple;
  }
  static findValueByAccessor(instance, accessor) {
    if (typeof accessor == "string") {
      const tuple = this.findInstanceByAccessor(instance, accessor);
      if (tuple) {
        instance = tuple[0];
        accessor = tuple[1];
        return instance[accessor];
      }
      return undefined;
    }
    const length = accessor.length;
    for (let i = 0; i < length; i++) {
      const value = Utility.findValueByAccessor(instance, accessor[i]);
      if (value !== undefined) {
        return value;
      }
    }
    return undefined;
  }
  static findStringValueByAccessor(instance, accessor) {
    if (typeof accessor == "string") {
      const value = Utility.findValueByAccessor(instance, accessor);
      if (value != null && typeof value == "string" && value.length > 0) {
        return value;
      }
      return null;
    }
    const length = accessor.length;
    for (let i = 0; i < length; i++) {
      const value = Utility.findValueByAccessor(instance, accessor[i]);
      if (value != null && typeof value == "string" && value.length > 0) {
        return value;
      }
    }
    return null;
  }
  static setValueByAccessor(instance, accessor, value) {
    const tuple = this.findInstanceByAccessor(instance, accessor);
    if (tuple) {
      instance = tuple[0];
      accessor = tuple[1];
      instance[accessor] = value;
    }
  }
  static getHashCode(value) {
    return HashCodeUtility.getHashCode(value);
  }
  /**
   * @param date Value to check for Dateness
   * @return Is the value a {@link Date} or a {@link string} representing a date.
   */
  static isDateType(date) {
    return date != null && (typeof date === "object" && date.constructor === Date || typeof date === "string" && !!date.match(AthenaeumConstants.dateRegex));
  }
  static restoreDate(model, path = "") {
    if (model != null) {
      if (typeof model === "object") {
        for (let property in model) {
          if (model.hasOwnProperty(property)) {
            const value = model[property];
            if (value != null) {
              let isDateStr = false;
              if (value.constructor === String) {
                // "yyyy-MM-ddThh:mm:ss.sssZ"
                // "2019-09-25T16:00:20.817Z"
                // "2019-09-25T16:00:20.817"
                // "2019-09-25"
                // "2019-09-24T00:00:00"
                // "2019-09-24T00:00:00Z"
                // "2019-10-14T21:00:00.000Z"
                // "2019-10-16T00:00:00+03:00"
                let dateStr = value;
                isDateStr = !!dateStr.match(AthenaeumConstants.dateRegex);
                if (isDateStr) {
                  const markedAsUtc = dateStr.endsWith("Z");
                  const includesTimezoneOffset = !markedAsUtc && dateStr.includes("+");
                  if (!includesTimezoneOffset) {
                    if (markedAsUtc) {
                      dateStr = dateStr.slice(0, -1);
                    }
                    const includesTime = dateStr.includes("T");
                    if (includesTime) {
                      const dateWithoutTime = !!dateStr.match(AthenaeumConstants.zeroTimeRegex);
                      if (!dateWithoutTime) {
                        // Time from server always in UTC!
                        dateStr += "Z";
                      }
                    } else {
                      dateStr += "T00:00:00Z";
                    }
                  }
                  model[property] = new Date(dateStr);
                }
              }
              if (!isDateStr) {
                this.restoreDate(value, path + "." + property);
              }
            }
          }
        }
      }
    }
    return model;
  }
  /**
   * @param object Value to clone.
   * @return Deep clone of the value.
   */
  static clone(object) {
    if (object == null) {
      return object;
    }
    const json = JSON.stringify(object);
    return JSON.parse(json);
  }
  static copyTo(from, ...to) {
    if (from == null) {
      return;
    }
    if (from instanceof Dictionary) {
      from.keys().forEach(key => {
        const value = from.getValue(key);
        to.forEach(instance => Utility.setValueByAccessor(instance, key, value));
      });
      return;
    }
    const copy = this.clone(from);
    for (const key in copy) {
      if (copy.hasOwnProperty(key)) {
        const value = Utility.findValueByAccessor(from, key);
        if (value !== undefined) {
          to.forEach(instance => Utility.setValueByAccessor(instance, key, value));
        }
      }
    }
  }
  static getExtensionsFromMimeTypes(mimeTypes) {
    if (mimeTypes.length && mimeTypes.every(type => type.includes("/"))) {
      return mimeTypes.map(type => type.split("/")[1]).join(", ");
    }
    return "";
  }
  /**
   * @returns A unique number.
   */
  static getId() {
    return ++this._number;
  }
  /**
   * @returns An underscore with a unique number appended to it.
   */
  static getComponentId() {
    return `_${this.getId()}`;
  }
  /**
   * @returns A new instance of the GUID string (a globally unique identifier).
   */
  static newGuid() {
    const d0 = Math.random() * 0xffffffff | 0;
    const d1 = Math.random() * 0xffffffff | 0;
    const d2 = Math.random() * 0xffffffff | 0;
    const d3 = Math.random() * 0xffffffff | 0;
    const lut = this._newGuidLut;
    if (lut.length == 0) {
      //initialize:
      for (let i = 0; i < 256; i++) {
        lut[i] = (i < 16 ? "0" : "") + i.toString(16);
      }
    }
    return lut[d0 & 0xff] + lut[d0 >> 8 & 0xff] + lut[d0 >> 16 & 0xff] + lut[d0 >> 24 & 0xff] + "-" + lut[d1 & 0xff] + lut[d1 >> 8 & 0xff] + "-" + lut[d1 >> 16 & 0x0f | 0x40] + lut[d1 >> 24 & 0xff] + "-" + lut[d2 & 0x3f | 0x80] + lut[d2 >> 8 & 0xff] + "-" + lut[d2 >> 16 & 0xff] + lut[d2 >> 24 & 0xff] + lut[d3 & 0xff] + lut[d3 >> 8 & 0xff] + lut[d3 >> 16 & 0xff] + lut[d3 >> 24 & 0xff];
  }
  static isGuid(value) {
    return !!value && !!value.match(AthenaeumConstants.guidRegex);
  }
  // ArrayUtility
  static where(items, predicate) {
    return ArrayUtility.where(items, predicate);
  }
  static whereAsync(items, callback) {
    return __awaiter(this, void 0, void 0, function* () {
      return ArrayUtility.whereAsync(items, callback);
    });
  }
  static selectMany(items, collectionSelector) {
    return ArrayUtility.selectMany(items, collectionSelector);
  }
  static chunk(items, size) {
    return ArrayUtility.chunk(items, size);
  }
  static take(items, count) {
    return ArrayUtility.take(items, count);
  }
  static takeLast(items, count) {
    return ArrayUtility.takeLast(items, count);
  }
  static takeWhile(items, predicate) {
    return ArrayUtility.takeWhile(items, predicate);
  }
  static skip(items, count) {
    return ArrayUtility.skip(items, count);
  }
  static firstOrDefault(items, callback, defaultValue) {
    return ArrayUtility.firstOrDefault(items, callback, defaultValue);
  }
  static lastOrDefault(items, callback, defaultValue) {
    return ArrayUtility.lastOrDefault(items, callback, defaultValue);
  }
  static forEachAsync(items, callback) {
    return __awaiter(this, void 0, void 0, function* () {
      return ArrayUtility.forEachAsync(items, callback);
    });
  }
  static groupBy(items, callback = null) {
    return ArrayUtility.groupBy(items, callback);
  }
  static remove(items, item) {
    ArrayUtility.remove(items, item);
  }
  static removeAt(items, index) {
    ArrayUtility.removeAt(items, index);
  }
  static max(items, callback = null) {
    return ArrayUtility.max(items, callback);
  }
  static maxValue(items, callback) {
    return ArrayUtility.maxValue(items, callback);
  }
  static min(items, callback = null) {
    return ArrayUtility.min(items, callback);
  }
  static minValue(items, callback) {
    return ArrayUtility.minValue(items, callback);
  }
  static sum(items, callback) {
    return ArrayUtility.sum(items, callback);
  }
  static count(items, predicate) {
    return ArrayUtility.count(items, predicate);
  }
  static distinct(items, callback) {
    return ArrayUtility.distinct(items, callback);
  }
  static toPagedList(items, pageNumber, pageSize) {
    return PagedListUtility.toPagedList(items, pageNumber, pageSize);
  }
  static sortByProperty(propertyName, sortDirection = SortDirection.Asc) {
    const direction = sortDirection == SortDirection.Desc ? -1 : 1;
    return (firstItem, secondItem) => {
      const x = this.findValueByAccessor(firstItem, propertyName);
      const y = this.findValueByAccessor(secondItem, propertyName);
      const result = x < y ? -1 : x > y ? 1 : 0;
      return result * direction;
    };
  }
  static reTryAsync(action, delay = 100, attempts = 3) {
    return __awaiter(this, void 0, void 0, function* () {
      try {
        return yield action();
      } catch (e) {
        if (attempts <= 0) {
          throw e;
        }
        if (delay > 0) {
          yield Utility.wait(100);
        }
        return this.reTryAsync(action, delay, attempts - 1);
      }
    });
  }
}
Utility._newGuidLut = [];
Utility._geoEnabled = null;
Utility._number = 1;
//Register Extensions
DateExtensions();
StringExtensions();
NumberExtensions();

class FileModel {
  constructor(src = null, type = null) {
    this.id = "";
    this.name = "";
    this.size = 0;
    this.type = "";
    this.src = "";
    this.description = null;
    this.lastModified = new Date();
    this.isFileModel = true;
    this.src = src || "";
    this.type = type || "";
  }
  static is(from) {
    return from != null && (from instanceof FileModel || from.isFileModel === true);
  }
  static as(from) {
    return FileModel.is(from) ? from : null;
  }
  static hasType(fileOrFileType, types) {
    if (fileOrFileType == null) {
      return false;
    }
    if (typeof fileOrFileType === "object") {
      return FileModel.hasType(fileOrFileType.type, types);
    }
    const fileTypeName = Utility.getFileTypeName(fileOrFileType);
    const fileTypesNames = types.split(",").map(type => Utility.getFileTypeName(type));
    const fileMimeType = Utility.getMimeType(fileTypeName);
    return types == "image/*" ? AthenaeumConstants.imageFileTypes.contains(fileMimeType) : fileTypesNames.contains(fileTypeName);
  }
}

class GeoLocation extends GeoCoordinate {
  constructor() {
    super(...arguments);
    this.country = "";
    this.address = "";
    this.city = "";
    this.postalCode = "";
    this.postalBox = "";
    this.formattedAddress = "";
    this.isGeoLocation = true;
  }
}

class NumberParsingResult {
  constructor(value = null) {
    this.value = 0;
    this.inputStr = "";
    this.acceptableStr = null;
    this.valueStr = "";
    this.parsed = false;
    if (value === null || value === undefined) {
      return;
    }
    this.value = value;
    this.valueStr = value.toString();
    this.inputStr = value.toString();
    this.acceptableStr = value.toString();
    this.parsed = true;
  }
}
class NumberUtility {
  static compare(x, y, inverse = false) {
    if (x < y) {
      return inverse ? 1 : -1;
    }
    if (x > y) {
      return inverse ? -1 : 1;
    }
    return 0;
  }
  static parse(str, allowFloat = true, maxLength = 12) {
    const info = new NumberParsingResult();
    info.inputStr = str;
    const escapedStr = str.replace(/([\n\r' ])/gm, "").replace(/([,])/gm, ".");
    const zeroValues = [".", "-", "+", "-.", "+."];
    let value = zeroValues.includes(escapedStr) ? 0 : Number(escapedStr);
    const parsed = !Number.isNaN(value) && Number.isFinite(value) && !escapedStr.includes("e") && !escapedStr.includes("E") && escapedStr.length <= maxLength && (allowFloat || Math.trunc(value) === value);
    const isAcceptableStr = parsed && (escapedStr.length === 0 || /^[-+]?[\d]*[.,]?[\d]*$/.test(escapedStr));
    value = parsed ? value : 0;
    const valueStr = parsed ? value.toString() : "";
    const acceptableStr = isAcceptableStr ? escapedStr : parsed ? valueStr : null;
    info.parsed = parsed;
    info.value = value;
    info.valueStr = valueStr;
    info.acceptableStr = acceptableStr;
    return info;
  }
  static resolveFormat(step, format, defaultFormat = null) {
    format = format || defaultFormat || "";
    if (format === "" && step != null) {
      format = step >= 0.1 && step < 1 ? "0.0" : step <= 0.01 ? "0.00" : "0";
    } else if (format && (step === null || step === undefined)) {
      const item = this._formats.find(item => item.format === format) || null;
      if (item) {
        return item;
      }
    }
    step = step || 1;
    return {
      step,
      format
    };
  }
}
NumberUtility._formats = [{
  format: "C",
  step: 0.01
}, {
  format: "c",
  step: 0.01
}, {
  format: "0.00",
  step: 0.01
}, {
  format: "0.00%",
  step: 0.01
}, {
  format: "0.0",
  step: 0.1
}, {
  format: "0.0%",
  step: 0.1
}, {
  format: "lat",
  step: 0.00000001
}, {
  format: "lon",
  step: 0.00000001
}, {
  format: "0",
  step: 1
}, {
  format: "n",
  step: 1
}, {
  format: "N",
  step: 1
}, {
  format: "%",
  step: 1
}, {
  format: "N%",
  step: 1
}, {
  format: "0%",
  step: 1
}];

class BoolUtility {
  static compare(x, y, inverse = false) {
    if (x < y) {
      return inverse ? 1 : -1;
    }
    if (x > y) {
      return inverse ? -1 : 1;
    }
    return 0;
  }
}

const ObjectType = "object";
class TypeConverter {
  static getServiceType(from, to) {
    const fromType = ServiceProvider$1.resolveServiceType(from);
    const toType = ServiceProvider$1.resolveServiceType(to);
    return `typeConverter:${fromType}:${toType}`;
  }
  addConverter(from, to, converter) {
    const fullName = TypeConverter.getServiceType(from, to);
    ServiceProvider$1.addSingleton(fullName, converter);
  }
  addObjectConverter(to, converter) {
    this.addConverter(ObjectType, to, converter);
  }
  getConverter(from, to) {
    const fullName = TypeConverter.getServiceType(from, to);
    return ServiceProvider$1.getService(fullName, false);
  }
  getObjectConverter(to) {
    return this.getConverter(ObjectType, to);
  }
  getRequiredConverter(from, to) {
    const fullName = TypeConverter.getServiceType(from, to);
    return ServiceProvider$1.getRequiredService(fullName, false);
  }
  getRequiredObjectConverter(to) {
    return this.getRequiredConverter(ObjectType, to);
  }
  canConvert(from, to) {
    const converter = this.getConverter(from, to) || this.getObjectConverter(to);
    return converter != null;
  }
  convert(from, to, defaultValue = null) {
    const converter = this.getConverter(from, to) || this.getObjectConverter(to);
    const value = converter != null ? typeof converter === "function" ? converter(from) : converter.convert(from) : null;
    return value != null ? value : defaultValue;
  }
}
const typeConverter = new TypeConverter();
function ObjectConverter(to) {
  return function decorator(target, propertyKey, descriptor) {
    const method = descriptor.value;
    if (to == null) {
      to = method.apply(target, [{}]);
    }
    if (to != null) {
      typeConverter.addObjectConverter(to, item => method.apply(target, [item]));
    }
  };
}

const StringType = "string";
function ToString(converter) {
  return constructor => {
    // implement class decorator here, the class decorator
    // will have access to the decorator arguments (filter)
    // because they are  stored in a closure
    typeConverter.addConverter(constructor, StringType, converter);
  };
}
class StringConverter {
  convert(item) {
    return this.toString(item);
  }
  toString(item, format) {
    if (item == null) {
      return "";
    }
    switch (typeof item) {
      case "string":
        return item;
      case "bigint":
      case "number":
      case "boolean":
        return item.toString();
    }
    if (Array.isArray(item)) {
      return item.length.toString();
    }
    const converter = this.getConverter(item);
    if (converter != null && converter !== this && converter !== this.toString) {
      return typeof converter === "function" ? converter(item, format) : converter.toString(item, format);
    }
    if (format) {
      if (typeof format === "function") {
        return format(item);
      }
      const enumProvider = ServiceProvider$1.findEnumProvider();
      if (enumProvider && enumProvider.isEnum(format)) {
        return enumProvider.getEnumText(format, item);
      }
      return Utility.format(`{0:${format}}`, item);
    }
    return typeof item.toString === "function" ? item.toString() : item;
  }
  addConverter(from, converter) {
    typeConverter.addConverter(from, StringType, converter);
  }
  addObjectConverter(converter) {
    typeConverter.addObjectConverter(StringType, converter);
  }
  getConverter(from) {
    return typeConverter.getConverter(from, StringType);
  }
  getObjectConverter() {
    return typeConverter.getObjectConverter(StringType);
  }
  getRequiredConverter(from) {
    return typeConverter.getRequiredConverter(from, StringType);
  }
  getRequiredObjectConverter() {
    return typeConverter.getRequiredObjectConverter(StringType);
  }
  canConvert(from) {
    return typeConverter.canConvert(from, StringType);
  }
}
var StringConverter$1 = new StringConverter();

class BaseTransformProvider {
  get selectListItemType() {
    if (this._selectListItemType == null) {
      const selectListItem = this.createSelectListItem("", "", "");
      this._selectListItemType = TypeResolver$1.resolve(selectListItem);
    }
    return this._selectListItemType;
  }
  constructor() {
    this._selectListItemType = null;
    // register the service
    ServiceProvider$1.addSingleton(this);
    // register converters
    // object to string converters:
    StringConverter$1.addObjectConverter((item, format) => this.toString(item, format));
    // object to ISelectListItem and SelectListItem converters
    typeConverter.addObjectConverter("ISelectListItem", item => this.toSelectListItem(item));
    typeConverter.addObjectConverter(this.selectListItemType, item => this.toSelectListItem(item));
  }
  initialize() {}
  getType() {
    return "ITransformProvider";
  }
  locationToString(location) {
    return location != null ? [location.address, location.postalCode, location.city].filter(item => !!item).join(", ") : "";
  }
  toString(item, format) {
    if (item == null) {
      return "";
    }
    if (item instanceof GeoLocation || item.isGeoLocation) {
      return this.locationToString(item);
    }
    return StringConverter$1.toString(item, format);
  }
  toSelectListItem(item) {
    var _a;
    if (typeof item === "number") {
      const value = item.toString();
      return this.createSelectListItem(value, value, "");
    }
    if (typeof item === "string") {
      return this.createSelectListItem(item, item, "");
    }
    const converter = (_a = typeConverter.getConverter(item, "ISelectListItem")) !== null && _a !== void 0 ? _a : typeConverter.getConverter(item, this.selectListItemType);
    if (converter != null && converter !== this.toSelectListItem) {
      return typeof converter === "function" ? converter(item) : converter.convert(item);
    }
    const value = Utility.findStringValueByAccessor(item, ["value"]);
    const id = Utility.findValueByAccessor(item, ["id", "code"]);
    const name = Utility.findStringValueByAccessor(item, ["name", "text", "label"]);
    const subtext = Utility.findStringValueByAccessor(item, ["subtext", "description"]);
    const groupName = Utility.findStringValueByAccessor(item, ["group", "group.name"]);
    const favorite = Utility.findStringValueByAccessor(item, "favorite") === "true";
    const itemValue = value ? value : id != null ? id.toString() : name ? name : Utility.getComponentId();
    return this.createSelectListItem(itemValue, name !== null && name !== void 0 ? name : "", subtext !== null && subtext !== void 0 ? subtext : "", groupName, favorite);
  }
}

class BaseEnumProvider {
  constructor() {
    // #region Private/Protected
    this._localizer = null;
    ServiceProvider$1.addSingleton(this);
  }
  getEnumLocalizedName(enumType, enumName) {
    return `Enum.${enumType}.${enumName}`;
  }
  getEnumLocalizedDescription(enumType, enumValueName) {
    const itemName = `Enum.${enumType}.${enumValueName}.Description`;
    return this.localizer.contains(itemName) ? itemName : "";
  }
  transform(enumSymbol, enumType, enumValue) {
    const value = enumValue;
    const enumName = enumSymbol[value];
    const text = this.getEnumLocalizedName(enumType, enumName);
    const subtext = this.getEnumLocalizedDescription(enumType, enumName);
    return this.createSelectListItem(value.toString(), text, subtext);
  }
  getItems(enumSymbol, enumType, reverse = false) {
    const items = this.getValues(enumSymbol, reverse);
    return items.map(value => this.transform(enumSymbol, enumType, value));
  }
  get localizer() {
    if (this._localizer) {
      return this._localizer;
    }
    this._localizer = ServiceProvider$1.findLocalizer();
    if (this._localizer) {
      return this._localizer;
    }
    throw new Error("EnumHelper. Localizer is not registered.");
  }
  // #endregion
  initialize() {}
  getType() {
    return "IEnumProvider";
  }
  getEnumItems(enumName, selectedValues, reverse) {
    const functionName = `get${enumName}Items`;
    const getter = this[functionName];
    const items = getter ? getter.call(this, !!reverse) : [];
    if (selectedValues != null && selectedValues.length > 0 && items.length > 0) {
      selectedValues.forEach(value => {
        const selectedItem = items.find(item => item.value === value.toString());
        if (selectedItem) {
          selectedItem.selected = true;
        }
      });
    }
    return items;
  }
  getEnumItem(enumName, value) {
    const values = this.getEnumItems(enumName);
    const enumValue = value.toString();
    const item = values.find(item => item.value === enumValue) || null;
    if (item == null) throw Error(`Select list item with value \"${value}\" for enum "${enumName}" cannot be found at BaseEnumProvider.getEnumItem.`);
    return item;
  }
  getEnumText(enumName, value) {
    const item = this.getEnumItem(enumName, value);
    return this.localizer.get(item.text);
  }
  getEnumDescription(enumName, value) {
    const item = this.getEnumItem(enumName, value);
    const description = item.subtext || item.text;
    return this.localizer.get(description);
  }
  getEnumName(enumName, value) {
    return this.getEnumItem(enumName, value).text;
  }
  isEnum(typeName) {
    return this.types.includes(typeName);
  }
  getValues(enumSymbol, reverse = false) {
    let items = Object.values(enumSymbol);
    if (reverse) {
      items = items.reverse();
    }
    return items.filter(value => !isNaN(value)).map(value => value);
  }
}

class PwaHelper {
  constructor() {
    this._deferredPrompt = null;
    this._initializeCallback = null;
    this._accepted = false;
  }
  onBeforeInstallPrompt(e) {
    // Prevent the mini info bar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later.
    this._deferredPrompt = e;
    // Callback
    if (this._initializeCallback) {
      this._initializeCallback();
      this._initializeCallback = null;
    }
  }
  installAsync() {
    return __awaiter(this, void 0, void 0, function* () {
      if (this._deferredPrompt) {
        // Show the install prompt
        yield this._deferredPrompt.prompt();
        // Wait for the user to respond to the prompt
        const result = yield this._deferredPrompt.userChoice;
        if (result.outcome === "accepted") {
          this._accepted = true;
          return true;
        }
      }
      return false;
    });
  }
  get initialized() {
    return !!this._deferredPrompt;
  }
  get canBeInstalled() {
    return this.initialized && !this._accepted;
  }
  subscribe(initializeCallback) {
    if (this.initialized) {
      initializeCallback();
    } else {
      this._initializeCallback = initializeCallback;
    }
  }
}
var PwaHelper$1 = new PwaHelper();

class Language {
  constructor(code = "", label = "") {
    this.code = "";
    this.label = "";
    this.isLanguage = true;
    this.code = code;
    this.label = label;
  }
}
/**
 * Base class for localizers.
 */
class BaseLocalizer {
  extractLanguage(item) {
    var _a;
    const obj = item;
    return (_a = obj.l) !== null && _a !== void 0 ? _a : obj.language;
  }
  extractValue(item) {
    var _a;
    const obj = item;
    return (_a = obj.v) !== null && _a !== void 0 ? _a : obj.value;
  }
  /**
   * @param supportedLanguages A list of supported languages.
   * @param defaultLanguageCode Language code of the default language (see {@link ILanguage.code}).
   */
  constructor(supportedLanguages, defaultLanguageCode) {
    this._items = new Dictionary(); // Language code -> Key -> Localized value
    this._supportedLanguages = supportedLanguages;
    this._supportedLanguageCodes = supportedLanguages.map(language => language.code);
    this._defaultLanguageCode = defaultLanguageCode;
    this._languageCode = defaultLanguageCode;
    ServiceProvider$1.addSingleton(this);
    //Initializer
    this.initialize();
  }
  /**
   * @return A list of languages supported by the {@link BaseLocalizer}.
   */
  getSupportedLanguages() {
    return this._supportedLanguages;
  }
  /**
   * @return A list of language codes supported by the {@link BaseLocalizer}.
   * @see ILanguage.code
   */
  getSupportedLanguageCodes() {
    return this._supportedLanguageCodes;
  }
  /**
   * @return Language code of the currently selected language.
   * @see ILanguage.code
   */
  getLanguage() {
    return this._languageCode;
  }
  /**
   * @return Language code of the {@link BaseLocalizer}'s default language.
   * @see ILanguage.code
   */
  getDefaultLanguage() {
    return this._defaultLanguageCode;
  }
  /**
   * Get all localized values of a language.
   *
   * @param languageCode Language code of the language which localized values to get (see {@link ILanguage.code}).
   * @return Localized values of the given language.
   */
  getLanguageItems(languageCode) {
    let languageItems = this._items.getValue(languageCode);
    if (!languageItems) {
      languageItems = new Dictionary();
      this._items.setValue(languageCode, languageItems);
    }
    return languageItems;
  }
  /**
   * Set a localized value.
   *
   * @param key Key which value to set.
   * @param languageCode Language code of the language which value to set (see {@link ILanguage.code}).
   * @param value Value to set.
   */
  setItem(key, languageCode, value) {
    if (this.language === languageCode) {
      const languageItems = this.getLanguageItems(languageCode);
      languageItems.setValue(key, value);
    }
  }
  /**
   * Set multiple different languages localized values which have the same key.
   *
   * @param key Key which values to set.
   * @param params Collection of Language code - Localized value pairs.
   */
  set(key, ...params) {
    params.map(item => this.setItem(key, this.extractLanguage(item), this.extractValue(item)));
  }
  /**
   * Initialize language dictionary (add localized items to a dictionary)
   */
  initialize(clear = false) {
    if (clear) {
      const items = this._items.values();
      const length = items.length;
      for (let i = 0; i < length; i++) {
        items[i].clear();
      }
    }
  }
  /**
   * Find a language with the given language code.
   * If no language is found, returns the default language.
   *
   * @param languageCode Language code of the language to find (see {@link ILanguage.code}).
   * @return A language with the given language code, or the default language.
   */
  findLanguage(languageCode) {
    const language = languageCode ? this.getSupportedLanguages().find(language => language.code === languageCode) : undefined;
    return language || this.findLanguage(this.getDefaultLanguage());
  }
  /**
   * A list of language codes supported by the {@link BaseLocalizer}.
   * @see ILanguage.code
   */
  get supportedLanguageCodes() {
    return this.getSupportedLanguageCodes();
  }
  /**
   * Language code of the {@link BaseLocalizer}'s default language.
   * @see ILanguage.code
   */
  get defaultLanguage() {
    return this.getDefaultLanguage();
  }
  // ILocalizer
  get language() {
    return this.getLanguage();
  }
  get supportedLanguages() {
    return this.getSupportedLanguages();
  }
  getValue(languageCode, key, ...params) {
    let value = null;
    if (key) {
      key = this.extractKeyFromName(key);
      const languageItems = this.getLanguageItems(languageCode);
      value = languageItems.getValue(key);
      if (value) {
        const lines = value.split(AthenaeumConstants.newLineRegex);
        value = lines.join("\n");
      }
    }
    value = value || key || "";
    return Utility.format(value, ...params);
  }
  get(key, ...params) {
    return this.getValue(this.getLanguage(), key, ...params);
  }
  extractKeyFromName(keyOrName) {
    const prefix = "_";
    if (keyOrName && keyOrName[0] !== prefix) {
      if (this.hasOwnProperty(keyOrName)) {
        const key = this[keyOrName];
        if (key) {
          return key;
        }
      } else if (keyOrName.includes(".")) {
        let key = keyOrName.replaceAll(".", "");
        key = key[0].toLowerCase() + key.substring(1);
        if (!key.endsWith("LanguageItemName")) {
          key = key + "LanguageItemName";
        }
        if (this.hasOwnProperty(key)) {
          key = this[key];
          if (key) {
            return key;
          }
        }
      }
    }
    return keyOrName;
  }
  contains(key, languageCode) {
    var _a;
    if (key) {
      key = this.extractKeyFromName(key);
      //languageCode: string = languageCode || this.getDefaultLanguage();
      languageCode = languageCode || this.getLanguage();
      const languageItems = (_a = this._items.getValue(languageCode)) !== null && _a !== void 0 ? _a : null;
      return languageItems != null && languageItems.containsKey(key);
    }
    return false;
  }
  setLanguage(languageCode) {
    if (this._languageCode !== languageCode) {
      if (!this._supportedLanguageCodes.includes(languageCode)) throw Error(`Unsupported language code "${languageCode}".`);
      this._languageCode = languageCode;
      this.initialize();
      return true;
    }
    return false;
  }
  // IService
  getType() {
    return "ILocalizer";
  }
}

export { AthenaeumConstants, BaseEnumProvider, BaseLocalizer, BaseTransformProvider, BoolUtility, DateExtensions, DateUtility, FileModel, GeoCoordinate, GeoLocation, HashCodeUtility, Language, NumberExtensions, NumberParsingResult, NumberUtility, ObjectConverter, PwaHelper$1 as PwaHelper, ServiceProvider$1 as ServiceProvider, SortDirection, StringConverter$1 as StringConverter, StringExtensions, StringUtility, TimeSpan, ToString, typeConverter as TypeConverter, TypeResolver$1 as TypeResolver, Utility };
